import { Dropdown } from '@platform/ui-library';
import { FC, useState } from 'react';
import { useDesignRoles } from '@app/hooks';
import { EDITOR_ROLES, UserCoreRole } from '@app/types';
import UserTypeStorage from '@app/utilities/UserTypeStorage';

export enum UserType {
  designer = 'designer',
  developer = 'developer',
  quality = 'quality',
  client = 'client'
};

const defaultUserType: UserType = UserType.client;

const userTypes = {
  designer: {
    label: 'Designer',
    mapToRole: UserCoreRole.designerDesigner,
    requiredRoles: EDITOR_ROLES
  },
  developer: {
    label: 'Developer',
    mapToRole: UserCoreRole.designerDeveloper,
    requiredRoles: EDITOR_ROLES
  },
  quality: {
    label: 'Quality',
    mapToRole: UserCoreRole.designerQa,
    requiredRoles: EDITOR_ROLES
  },
  client: {
    label: 'Client',
    mapToRole: null,
    requiredRoles: []
  }
} satisfies Record<
  UserType,
  {
    label: string;
    mapToRole: UserCoreRole | null;
    requiredRoles: UserCoreRole[];
  }
>;

export const UserTypeSelector: FC = () => {
  const [storedUserType, setStoredUserType] = useState<UserType>(UserTypeStorage.getUserType());
  const { hasDesignRole } = useDesignRoles();
  const setUserType = (userTypeKey: UserType): void => {
    setStoredUserType(userTypeKey);
    UserTypeStorage.setUserType(userTypeKey);
    document.dispatchEvent(new CustomEvent('ev-update-userType'));
  };

  let initialUserType: UserType | null = null;
  const options = Object.fromEntries(
    Object.entries(userTypes)
      .filter(([role, { mapToRole, requiredRoles }]) => {
        const isValid = hasDesignRole(requiredRoles);
        if (isValid && mapToRole && hasDesignRole(mapToRole)) {
          initialUserType = role as UserType;
        }
        return isValid;
      })
      .map(([role, { label }]) => {
        return [role, label];
      })
  );

  const currentUserType = storedUserType ?? initialUserType ?? defaultUserType;

  if (Object.keys(options).length <= 1) {
    return null;
  }

  return (
    <Dropdown
      options={options}
      onChange={(role) => {
        setUserType(role as UserType);
      }}
      value={currentUserType}
      className="selector"
    />
  );
};
